import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Clock, Copy, Package, XCircle } from 'lucide-react';
import AppIcon from '../components/AppIcon';
import { getErrorMessage } from '../api/errors';
import { ORDER_STATUS_LABELS, useOrders } from '../context/OrderContext';
import { useProducts } from '../context/ProductContext';
import { ordersService } from '../services';
import { formatAmount } from '../utils/formatPrice';
import { formatOrderNumberLabel } from '../utils/formatOrderNumber';
import { getProductUrl, resolveProductImageUrl } from '../utils/product';
import {
  CANCELLATION_REASON_MAX,
  CANCELLATION_REASON_MIN,
  ORDER_REQUEST_STATUS_LABELS,
  ORDER_REQUEST_TYPE_LABELS,
  canRequestCancellation,
  getLatestCancellationRequest,
  getPendingCancellationRequest,
  validateCancellationReason,
} from '../utils/orderRequests';

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function Orders() {
  const { orders, isLoading, error, refreshOrders } = useOrders();
  const { getProductById } = useProducts();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState('');
  const [copiedTrackingId, setCopiedTrackingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');
  const [cancellationDrafts, setCancellationDrafts] = useState<Record<string, string>>({});
  const [submittingCancellationId, setSubmittingCancellationId] = useState<string | null>(null);
  const [withdrawingRequestId, setWithdrawingRequestId] = useState<string | null>(null);
  const [showCancellationFormId, setShowCancellationFormId] = useState<string | null>(null);

  useEffect(() => {
    void refreshOrders({ silent: orders.length > 0 });
    // Yalnızca sayfaya her gelişte yenile; orders.length deps'e eklenmez (çift istek olur)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshOrders]);

  const handleDownloadInvoice = async (orderId: string) => {
    setDownloadingId(orderId);
    setDownloadError('');
    try {
      await ordersService.downloadOrderInvoice(orderId);
    } catch (err) {
      setDownloadError(getErrorMessage(err, 'Fatura indirilemedi.'));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleCopyTracking = async (orderId: string, trackingNumber: string) => {
    try {
      await navigator.clipboard.writeText(trackingNumber);
      setCopiedTrackingId(orderId);
      window.setTimeout(() => {
        setCopiedTrackingId((current) => (current === orderId ? null : current));
      }, 2000);
    } catch {
      setActionError('Takip numarası kopyalanamadı.');
    }
  };

  const handleSubmitCancellation = async (orderId: string) => {
    const reason = cancellationDrafts[orderId] ?? '';
    const validationError = validateCancellationReason(reason);
    if (validationError) {
      setActionError(validationError);
      return;
    }

    setSubmittingCancellationId(orderId);
    setActionError('');
    try {
      await ordersService.submitCancellationRequest(orderId, reason.trim());
      setShowCancellationFormId(null);
      setCancellationDrafts((prev) => ({ ...prev, [orderId]: '' }));
      await refreshOrders({ silent: true });
    } catch (err) {
      setActionError(getErrorMessage(err, 'İptal talebi gönderilemedi.'));
    } finally {
      setSubmittingCancellationId(null);
    }
  };

  const handleWithdrawCancellation = async (orderId: string, requestId: string) => {
    setWithdrawingRequestId(requestId);
    setActionError('');
    try {
      await ordersService.withdrawOrderRequest(orderId, requestId);
      await refreshOrders({ silent: true });
    } catch (err) {
      setActionError(getErrorMessage(err, 'İptal talebi geri çekilemedi.'));
    } finally {
      setWithdrawingRequestId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="container empty-state">
        <p>Siparişleriniz yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container empty-state">
        <div className="empty-cart">
          <h2>Siparişler yüklenemedi</h2>
          <p>{error}</p>
          <button type="button" className="btn btn-primary" onClick={() => void refreshOrders()}>
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="container empty-state">
        <div className="empty-cart">
          <span className="empty-icon">
            <AppIcon icon={Package} size={64} strokeWidth={1.5} />
          </span>
          <h2>Henüz siparişiniz yok</h2>
          <p>Verdiğiniz siparişler burada listelenecektir.</p>
          <Link to="/shop" className="btn btn-primary">Alışverişe Başla</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container orders-page">
      <div className="page-header">
        <h1>Siparişlerim</h1>
        <p>{orders.length} sipariş</p>
      </div>

      {downloadError && <div className="auth-error">{downloadError}</div>}
      {actionError && <div className="auth-error">{actionError}</div>}

      <div className="orders-list">
        {orders.map((order) => {
          const pendingCancellation = getPendingCancellationRequest(order);
          const latestCancellation = getLatestCancellationRequest(order);
          const canCancel = canRequestCancellation(order);
          const showCancellationForm = showCancellationFormId === order.id;

          return (
          <article key={order.id} className="order-card">
            <div className="order-card-header">
              <div>
                <span className="order-id">{formatOrderNumberLabel(order)}</span>
                <span className="order-date">{formatDate(order.createdAt)}</span>
              </div>
              <span className={`order-status status-${order.status}`}>
                {ORDER_STATUS_LABELS[order.status]}
              </span>
            </div>

            <div className="order-items">
              {order.items.map((item) => {
                const productSlug = getProductById(item.productId)?.slug;
                const productLink = productSlug
                  ? getProductUrl(productSlug)
                  : `/product/${item.productId}`;

                return (
                <div key={item.productId} className="order-item">
                  <img src={resolveProductImageUrl(item.productImage)} alt={item.productName} />
                  <div className="order-item-info">
                    <Link to={productLink}>{item.productName}</Link>
                    <span>Adet: {item.quantity}</span>
                  </div>
                  <span className="order-item-price">
                    {formatAmount(item.price * item.quantity, item.currency)}
                  </span>
                </div>
                );
              })}
            </div>

            <div className="order-card-footer">
              <div className="order-address">
                <strong>Teslimat:</strong>{' '}
                {order.shippingAddress.address}, {order.shippingAddress.district},{' '}
                {order.shippingAddress.city} {order.shippingAddress.zip}
                {order.billingSameAsShipping === false && order.billingAddress && (
                  <div className="order-billing-info">
                    <strong>Fatura:</strong>{' '}
                    {order.billingAddress.address}, {order.billingAddress.district},{' '}
                    {order.billingAddress.city} {order.billingAddress.zip}
                  </div>
                )}
                {order.trackingNumber && (
                  <div className="order-shipping-info">
                    {order.shippingCarrierName && (
                      <span className="order-carrier">{order.shippingCarrierName}</span>
                    )}
                    <span className="order-tracking">
                      Kargo takip no: <strong>{order.trackingNumber}</strong>
                      <button
                        type="button"
                        className="order-copy-tracking"
                        aria-label="Kargo takip numarasını kopyala"
                        onClick={() => void handleCopyTracking(order.id, order.trackingNumber!)}
                      >
                        <AppIcon
                          icon={copiedTrackingId === order.id ? Check : Copy}
                          size={14}
                          strokeWidth={2}
                        />
                        {copiedTrackingId === order.id ? 'Kopyalandı' : 'Kopyala'}
                      </button>
                    </span>
                  </div>
                )}
              </div>
              <div className="order-totals">
                {(order.discountAmount ?? 0) > 0 && (
                  <span className="order-discount">
                    İndirim{order.couponCode ? ` (${order.couponCode})` : ''}: −
                    {formatAmount(order.discountAmount, order.currency)}
                  </span>
                )}
                <div className="order-total">
                  Toplam: <strong>{formatAmount(order.total, order.currency)}</strong>
                </div>
                {order.invoiceAvailable && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm order-invoice-btn"
                    disabled={downloadingId === order.id}
                    onClick={() => void handleDownloadInvoice(order.id)}
                  >
                    {downloadingId === order.id ? 'İndiriliyor...' : 'Faturayı İndir'}
                  </button>
                )}
              </div>
            </div>

            {(canCancel || pendingCancellation || latestCancellation) && (
              <div className="order-cancellation">
                {pendingCancellation ? (
                  <div className="order-cancel-panel order-cancel-panel-pending">
                    <div className="order-cancel-panel-head">
                      <span className="order-cancel-panel-icon" aria-hidden>
                        <AppIcon icon={Clock} size={18} strokeWidth={2} />
                      </span>
                      <div className="order-cancel-panel-meta">
                        <span className="order-cancel-panel-title">
                          {ORDER_REQUEST_TYPE_LABELS[pendingCancellation.type]}
                        </span>
                        <span className="order-cancel-panel-badge">
                          {ORDER_REQUEST_STATUS_LABELS[pendingCancellation.status]}
                        </span>
                      </div>
                    </div>
                    <div className="order-cancel-panel-reason">
                      <span>İptal nedeni</span>
                      <p>{pendingCancellation.reason}</p>
                    </div>
                    <div className="order-cancel-panel-foot">
                      <p>Talebiniz inceleniyor. İsterseniz geri çekebilirsiniz.</p>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm order-cancel-panel-withdraw"
                        disabled={withdrawingRequestId === pendingCancellation.id}
                        onClick={() =>
                          void handleWithdrawCancellation(order.id, pendingCancellation.id)
                        }
                      >
                        {withdrawingRequestId === pendingCancellation.id
                          ? 'Geri çekiliyor...'
                          : 'Talebi Geri Çek'}
                      </button>
                    </div>
                  </div>
                ) : canCancel ? (
                  <>
                    {!showCancellationForm ? (
                      <div className="order-cancel-panel order-cancel-panel-action">
                        <div className="order-cancel-panel-head">
                          <span className="order-cancel-panel-icon" aria-hidden>
                            <AppIcon icon={XCircle} size={18} strokeWidth={2} />
                          </span>
                          <p className="order-cancel-panel-lead">
                            Henüz kargoya verilmemiş siparişinizi iptal edebilirsiniz.
                          </p>
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          onClick={() => setShowCancellationFormId(order.id)}
                        >
                          İptal Talebi Oluştur
                        </button>
                      </div>
                    ) : (
                      <div className="order-cancel-panel order-cancel-panel-form">
                        <div className="order-cancel-panel-head">
                          <span className="order-cancel-panel-icon" aria-hidden>
                            <AppIcon icon={XCircle} size={18} strokeWidth={2} />
                          </span>
                          <div className="order-cancel-panel-meta">
                            <span className="order-cancel-panel-title">İptal Talebi Oluştur</span>
                            <span className="order-cancel-panel-subtitle">
                              Henüz kargoya verilmemiş siparişler için
                            </span>
                          </div>
                        </div>
                        <label className="sr-only" htmlFor={`cancel-reason-${order.id}`}>
                          İptal nedeni
                        </label>
                        <textarea
                          id={`cancel-reason-${order.id}`}
                          rows={3}
                          maxLength={CANCELLATION_REASON_MAX}
                          placeholder="Örn. Yanlışlıkla sipariş verdim, henüz kargoya verilmedi."
                          value={cancellationDrafts[order.id] ?? ''}
                          onChange={(event) =>
                            setCancellationDrafts((prev) => ({
                              ...prev,
                              [order.id]: event.target.value,
                            }))
                          }
                        />
                        <p className="order-cancel-panel-hint">
                          En az {CANCELLATION_REASON_MIN}, en fazla {CANCELLATION_REASON_MAX} karakter
                        </p>
                        <div className="order-cancel-panel-actions">
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={() => {
                              setShowCancellationFormId(null);
                              setCancellationDrafts((prev) => ({ ...prev, [order.id]: '' }));
                            }}
                          >
                            Vazgeç
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={submittingCancellationId === order.id}
                            onClick={() => void handleSubmitCancellation(order.id)}
                          >
                            {submittingCancellationId === order.id
                              ? 'Gönderiliyor...'
                              : 'İptal Talebini Gönder'}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : latestCancellation ? (
                  <div
                    className={`order-cancel-panel order-cancel-panel-resolved order-cancel-panel-${latestCancellation.status}`}
                  >
                    <div className="order-cancel-panel-head">
                      <div className="order-cancel-panel-meta">
                        <span className="order-cancel-panel-title">
                          {ORDER_REQUEST_TYPE_LABELS[latestCancellation.type]}
                        </span>
                        <span className="order-cancel-panel-badge">
                          {ORDER_REQUEST_STATUS_LABELS[latestCancellation.status]}
                        </span>
                      </div>
                    </div>
                    {latestCancellation.adminNote && (
                      <div className="order-cancel-panel-reason">
                        <span>Yanıt</span>
                        <p>{latestCancellation.adminNote}</p>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </article>
          );
        })}
      </div>
    </div>
  );
}
